import { Turnstile } from '@marsidev/react-turnstile';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api } from '../api.js';
import { useAuth } from '../auth/AuthProvider.js';

interface InviteMeta {
  email: string;
  role: string;
  orgName: string;
  inviterName: string | null;
  expiresAt: string;
}

export function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const { t } = useTranslation('auth');
  const [meta, setMeta] = useState<InviteMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    api<InviteMeta>(`/invitations/${token}`)
      .then(setMeta)
      .catch((e: { body?: { error?: string }; message?: string }) =>
        setError(e.body?.error ?? e.message ?? 'invitation_not_found'),
      );
  }, [token]);

  if (error) return <p className="m-8 text-sm">{error}</p>;
  if (!meta) return <p className="m-8 text-sm">{t('common:loading')}</p>;

  if (!user) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded border bg-white p-6">
        <p className="mb-4">
          {t('joinOrg', { orgName: meta.orgName })} — {meta.role}
        </p>
        <p className="mb-2 text-sm">
          <Link
            className="text-blue-600"
            to={`/signup?invite=${token}&email=${encodeURIComponent(meta.email)}`}
          >
            {t('signup')}
          </Link>
          {' / '}
          <Link className="text-blue-600" to={`/login?return=/accept-invite/${token}`}>
            {t('login')}
          </Link>
        </p>
      </div>
    );
  }

  if (user.email.toLowerCase() !== meta.email.toLowerCase()) {
    return (
      <p className="m-8 text-sm">
        Esta invitación es para {meta.email}, pero tu cuenta es {user.email}.
      </p>
    );
  }

  const accept = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await api<{ orgId: string }>(`/invitations/${token}/accept`, {
        method: 'POST',
        headers: captcha ? { 'cf-turnstile-response': captcha } : {},
      });
      await api('/orgs/switch', {
        method: 'POST',
        body: JSON.stringify({ orgId: res.orgId }),
      });
      window.location.href = '/app';
    } catch (e: unknown) {
      setError((e as { body?: { error?: string } }).body?.error ?? 'invitation_failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto mt-16 max-w-md rounded border bg-white p-6">
      <p className="mb-4">
        {t('joinOrg', { orgName: meta.orgName })} — {meta.role}
      </p>
      <Turnstile siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''} onSuccess={setCaptcha} />
      <button
        type="button"
        disabled={busy || !captcha}
        onClick={() => void accept()}
        className="mt-3 rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
      >
        {t('acceptInvite')}
      </button>
    </div>
  );
}
